import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { toast } from 'react-hot-toast';
import { Mail, Lock, User, Phone, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import logo3D from '../../../assets/logos/3D-LOGO.png';
import logoUDSM from '../../../assets/logos/UDSM-LOGO.png';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const getPasswordChecks = (password) => ({
  minLength: password.length >= 8,
  uppercase: /[A-Z]/.test(password),
  lowercase: /[a-z]/.test(password),
  number: /[0-9]/.test(password),
  special: /[^A-Za-z0-9]/.test(password)
});

const getPasswordStrength = (password) => {
  const checks = getPasswordChecks(password);
  const score = Object.values(checks).filter(Boolean).length;

  if (!password) {
    return { score: 0, label: 'No password', color: 'bg-gray-200' };
  }

  if (score <= 2) {
    return { score, label: 'Weak', color: 'bg-rose-500' };
  }

  if (score <= 4) {
    return { score, label: 'Moderate', color: 'bg-amber-500' };
  }

  return { score, label: 'Strong', color: 'bg-emerald-500' };
};

const SignUp = () => {
  const navigate = useNavigate();
  const { signup } = useApiAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'community_member',
    authCode: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const passwordChecks = getPasswordChecks(formData.password);
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    // Validate password strength on frontend
    if (!passwordChecks.uppercase) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!passwordChecks.lowercase) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    if (!passwordChecks.number) {
      toast.error('Password must contain at least one digit');
      return;
    }
    if (!passwordChecks.special) {
      toast.error('Password must contain at least one special character');
      return;
    }

    try {
      setLoading(true);
      
      const signupData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        role: formData.role,
        phone: formData.phone || undefined,
        auth_code: formData.authCode || undefined
      };

      const response = await signup(signupData);
      
      if (response.requires_approval) {
        // User needs approval
        toast.success('Account created! Waiting for admin approval.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        // User is immediately active (admin/client)
        toast.success('Account created successfully!');
        navigate('/');
      }
    } catch (error) {
      console.error('Signup error:', error);
      // Show error message from backend
      const errorMsg = error.error || error.detail || error.message || 'Signup failed';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const roleRequiresAuthCode = formData.role === 'admin' || formData.role === 'client';
  const passwordChecks = getPasswordChecks(formData.password);
  const passwordStrength = getPasswordStrength(formData.password);
  const passwordMatch = formData.confirmPassword && formData.password === formData.confirmPassword;

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: NEUTRAL_COLORS.white }}>
      <div className="grid min-h-screen lg:grid-cols-[1fr_1.2fr]">
        <div className="hidden p-10 lg:flex xl:p-12" style={{ backgroundColor: BRAND_COLORS.primary }}>
          <div className="flex flex-col w-full min-h-full">
            <div className="flex items-center gap-4">
              <img src={logo3D} alt="3D Lab logo" className="h-12 w-12 rounded-lg bg-white p-1.5" />
              <img src={logoUDSM} alt="UDSM logo" className="h-12 w-12 rounded-lg bg-white p-1.5" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: SYSTEM_COLORS.textOnBrandMedium }}>
                Registration
              </span>
            </div>

            <div className="max-w-md my-auto">
              
              <h1 className="mt-4 text-4xl font-semibold leading-tight" style={{ color: NEUTRAL_COLORS.white }}>Join A.M.A.R.M.S</h1>
              <p className="mt-4 text-base leading-7" style={{ color: SYSTEM_COLORS.textOnBrandStrong }}>
                Active Monitoring and Resource Management System
              </p>
            </div>
             <div className="pt-5 border-t" style={{ borderColor: SYSTEM_COLORS.borderOnBrand }}>
              <p className="text-sm font-medium" style={{ color: SYSTEM_COLORS.textOnBrandStrong }}>
                Developed and Managed by{' '}
                <a
                  href="https://robotics.udsm.ac.tz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold "
                  style={{ color: BRAND_COLORS.accent, textDecorationColor: SYSTEM_COLORS.accentTint }}
                >
                  3D Solutions and Robotics Lab - University of Dar es Salaam
                </a>
              </p>
            </div>

          </div>
        </div>

        <div className="px-4 py-8 sm:px-8 sm:py-10 lg:px-10 lg:max-h-screen lg:overflow-y-auto">
          <div className="w-full max-w-xl mx-auto">
            <div className="flex justify-center gap-3 mb-6 lg:hidden">
              <img src={logo3D} alt="3D Lab logo" className="w-10 h-10 p-1 border rounded-lg" style={{ borderColor: SYSTEM_COLORS.border }} />
              <img src={logoUDSM} alt="UDSM logo" className="w-10 h-10 p-1 border rounded-lg" style={{ borderColor: SYSTEM_COLORS.border }} />
            </div>
            <p className="lg:hidden mb-6 text-center text-[11px] font-semibold uppercase tracking-[0.16em] sm:text-xs sm:tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
              A.M.A.R.M.S Platform
            </p>
            <p className="text-sm font-semibold leading-6 text-center lg:hidden mb-7" style={{ color: NEUTRAL_COLORS.black }}>
              Active Monitoring and Resource Management System
            </p>

            <div className="mb-6 sm:mb-7">
              <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>Create Account</p>
              <h2 className="mt-2 text-2xl font-bold sm:text-3xl" style={{ color: NEUTRAL_COLORS.black }}>Set up your profile</h2>
              <p className="mt-2 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
                Already have an account?{' '}
                <Link to="/login" className="font-semibold underline underline-offset-4" style={{ color: BRAND_COLORS.primary, textDecorationColor: SYSTEM_COLORS.primaryTint }}>
                  Sign in
                </Link>
              </p>
            </div>

            <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Full Name</label>
                <div className="relative">
                  <User className="absolute -translate-y-1/2 left-3 top-1/2" size={18} style={{ color: BRAND_COLORS.primary }} />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="block w-full py-3 pl-10 pr-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                      style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                    placeholder="Enter your full name"
                  />
                </div>
              </div>

                <div>
                  <label htmlFor="email" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Email address</label>
                  <div className="relative">
                    <Mail className="absolute -translate-y-1/2 left-3 top-1/2" size={18} style={{ color: BRAND_COLORS.primary }} />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full py-3 pl-10 pr-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                      style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                      placeholder="name@organization.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute -translate-y-1/2 left-3 top-1/2" size={18} style={{ color: BRAND_COLORS.primary }} />
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="block w-full py-3 pl-10 pr-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                      style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="role" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Role</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="block w-full px-3 py-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                    style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                  >
                    <option value="community_member">Community Member</option>
                    <option value="developer">Developer</option>
                    <option value="leader">Team Leader</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="hod">Head of Department</option>
                    <option value="pr">Public Relations</option>
                    <option value="marketing">Marketing</option>
                    <option value="graphics">Graphics Designer</option>
                    <option value="resource_manager">Resource Manager</option>
                    <option value="client">Client</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>

                {roleRequiresAuthCode && (
                  <div>
                    <label htmlFor="authCode" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>
                      Authorization Code
                    </label>
                    <input
                      id="authCode"
                      name="authCode"
                      type="text"
                      required
                      value={formData.authCode}
                      onChange={handleChange}
                      className="block w-full px-3 py-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                      style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                      placeholder="Required for selected role"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="password" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Password</label>
                  <div className="relative">
                    <Lock className="absolute -translate-y-1/2 left-3 top-1/2" size={18} style={{ color: BRAND_COLORS.primary }} />
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="block w-full py-3 pl-10 pr-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                      style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                      placeholder="Create a strong password"
                    />
                  </div>

                  <div className="p-3 mt-3 border rounded-xl" style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: BRAND_COLORS.primary }}>Password Strength</p>
                      <span className="text-xs font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{passwordStrength.label}</span>
                    </div>
                    <div className="w-full h-2 overflow-hidden bg-gray-200 rounded-full">
                      <div
                        className={`h-2 transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      />
                    </div>
                    <div className="grid gap-1 mt-3">
                      <p className="flex items-center gap-2 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                        {passwordChecks.minLength ? <CheckCircle2 size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-rose-500" />}
                        At least 8 characters
                      </p>
                      <p className="flex items-center gap-2 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                        {passwordChecks.uppercase ? <CheckCircle2 size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-rose-500" />}
                        One uppercase letter
                      </p>
                      <p className="flex items-center gap-2 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                        {passwordChecks.lowercase ? <CheckCircle2 size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-rose-500" />}
                        One lowercase letter
                      </p>
                      <p className="flex items-center gap-2 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                        {passwordChecks.number ? <CheckCircle2 size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-rose-500" />}
                        One number
                      </p>
                      <p className="flex items-center gap-2 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                        {passwordChecks.special ? <CheckCircle2 size={14} className="text-emerald-500" /> : <XCircle size={14} className="text-rose-500" />}
                        One special character
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block mb-2 text-sm font-medium" style={{ color: NEUTRAL_COLORS.black }}>Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute -translate-y-1/2 left-3 top-1/2" size={18} style={{ color: BRAND_COLORS.primary }} />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="block w-full py-3 pl-10 pr-3 text-sm bg-white border rounded-xl sm:text-base focus:outline-none focus:ring-2"
                      style={{ borderColor: SYSTEM_COLORS.border, color: NEUTRAL_COLORS.black, '--tw-ring-color': SYSTEM_COLORS.primaryRing }}
                      placeholder="Re-enter your password"
                    />
                  </div>
                  {formData.confirmPassword && !passwordMatch && (
                    <p className="mt-2 text-xs text-rose-600">Passwords do not match yet.</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center w-full gap-2 px-4 py-3 text-sm font-semibold text-white transition rounded-xl disabled:opacity-60"
                  style={{ backgroundColor: BRAND_COLORS.primary }}
                >
                  {loading ? 'Creating account...' : 'Create account'}
                  {!loading && <ArrowRight size={16} />}
                </button>

                <p className="pb-3 text-xs text-center" style={{ color: SYSTEM_COLORS.textMuted }}>
                  By signing up, you agree to platform usage and security policies.
                </p>
            </form>

            <p className="mt-6 text-xs leading-5 text-center lg:hidden" style={{ color: SYSTEM_COLORS.textMuted }}>
              Developed and managed by{' '}
              <a
                href="https://robotics.udsm.ac.tz"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold"
                style={{ color: BRAND_COLORS.accent, textDecorationColor: SYSTEM_COLORS.accentTint }}
              >
                3D Solutions and Robotics Lab - University of Dar es Salaam
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
